import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories, news, site } from "@/mocks/content";
import { catOf } from "@/components/feature/Cards";

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 font-heading text-lg font-black text-background-50">
        瓜
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-heading text-xl font-black tracking-tight text-foreground-900">
          今日新瓜
        </span>
        <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary-600">
          Exclusive Insider
        </span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [searchOpen]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return news
      .filter((n) => {
        const c = catOf(n.category);
        return (
          n.title.toLowerCase().includes(q) ||
          n.lead.toLowerCase().includes(q) ||
          n.tags.some((t) => t.toLowerCase().includes(q)) ||
          c.name.includes(query.trim())
        );
      })
      .slice(0, 6);
  }, [query]);

  const go = (id: number) => {
    navigate(`/news/${id}`);
    setSearchOpen(false);
    setQuery("");
  };

  return (
    <header className="sticky top-0 z-40">
      {/* 顶部公告条 */}
      <div className="bg-foreground-950 text-background-100">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 md:px-6">
          <p className="flex min-w-0 items-center gap-2 text-xs">
            <i className="ri-flashlight-fill shrink-0 text-accent-400" />
            <span className="truncate">
              {site.slogan} · 深挖事件背后不为人知的内幕真相
            </span>
          </p>
          <div className="hidden shrink-0 items-center gap-4 text-xs text-background-200 sm:flex">
            <Link to="/about" className="transition-colors hover:text-accent-300">
              关于我们
            </Link>
            <Link to="/faq" className="transition-colors hover:text-accent-300">
              常见问题
            </Link>
          </div>
        </div>
      </div>

      {/* 主导航 */}
      <div
        className={`border-b bg-background-50/95 backdrop-blur transition-shadow ${
          scrolled ? "border-background-200 shadow-sm" : "border-background-100"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
          <Brand />

          <nav
            className="hidden items-center gap-1 lg:flex"
            aria-label="主导航"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
            >
              <i className="ri-home-4-line" /> 首页
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
              >
                <i className={c.icon} />
                {c.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-200 text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
            >
              <i className="ri-search-line" />
            </button>
            <Link
              to="/category/exclusive"
              className="hidden items-center gap-1 whitespace-nowrap rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 md:inline-flex"
            >
              <i className="ri-flashlight-fill" /> 独家爆料
            </Link>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="菜单"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-200 text-foreground-700 lg:hidden"
            >
              <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"} />
            </button>
          </div>
        </div>

        {/* 移动端菜单 */}
        {mobileOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <nav className="mx-auto grid max-w-7xl grid-cols-2 gap-1 px-4 py-4 md:px-6" aria-label="移动端导航">
              <Link
                to="/"
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-home-4-line" /> 首页
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
                >
                  <i className={c.icon} /> {c.name}
                </Link>
              ))}
              <Link
                to="/about"
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-information-line" /> 关于我们
              </Link>
              <Link
                to="/faq"
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-question-line" /> 常见问题
              </Link>
            </nav>
          </div>
        )}
      </div>

      {/* 全站搜索抽屉 */}
      {searchOpen && (
        <div className="absolute inset-x-0 top-full border-b border-background-200 bg-background-50/98 shadow-md backdrop-blur">
          <div className="mx-auto max-w-3xl px-4 py-4 md:px-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (results.length) go(results[0].id);
              }}
              className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5 focus-within:border-primary-400"
            >
              <i className="ri-search-line text-lg text-foreground-500" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索独家内幕、明星动态、综艺热搜……"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="清空"
                  className="text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-circle-fill" />
                </button>
              )}
            </form>

            <div className="mt-3">
              {query && results.length === 0 && (
                <p className="px-2 py-4 text-center text-sm text-foreground-500">
                  没有找到“{query}”相关内容，换个关键词试试。
                </p>
              )}
              {results.length > 0 && (
                <ul className="divide-y divide-background-100">
                  {results.map((r) => {
                    const c = catOf(r.category);
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => go(r.id)}
                          className="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-background-100"
                        >
                          <img
                            src={r.cover}
                            alt=""
                            className="h-12 w-16 shrink-0 rounded-md object-cover"
                          />
                          <span className="min-w-0">
                            <span className="line-clamp-1 text-sm font-semibold text-foreground-900">
                              {r.title}
                            </span>
                            <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                              <span className="inline-flex items-center gap-1 text-primary-600">
                                <i className={c.icon} /> {c.name}
                              </span>
                              {r.time}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {!query && (
                <div className="flex flex-wrap items-center gap-2 px-2 pb-1">
                  <span className="text-xs text-foreground-500">大家都在搜：</span>
                  {["独家内幕", "综艺", "反转", "红毯", "巡演"].map((t) => (
                    <button
                      key={t}
                      onClick={() => setQuery(t)}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}